/**
 * Displays per-form DTA creation progress during the batch creation step, and doubles as the
 * completion summary view. Shows status, links to created DTAs, and error messages for failures.
 */

import Alert from "@cloudscape-design/components/alert"
import Box from "@cloudscape-design/components/box"
import Button from "@cloudscape-design/components/button"
import Link from "@cloudscape-design/components/link"
import SpaceBetween from "@cloudscape-design/components/space-between"
import StatusIndicator, {
  type StatusIndicatorProps,
} from "@cloudscape-design/components/status-indicator"
import Table from "@cloudscape-design/components/table"
import type { CreationProgress, CreationStatus, ParsedFormResult } from "./types"

interface CreationProgressStepProps {
  /**
   * All forms from the parse result (used to look up labels).
   */
  forms: ParsedFormResult[]
  /**
   * Which forms were selected for creation.
   */
  selectedForms: Set<string>
  /**
   * Current creation status per form name.
   */
  progress: Map<string, CreationProgress>
  /**
   * Whether all creation work has finished (determines if we show the summary line and action
   * buttons).
   */
  isComplete: boolean
  /**
   * Count of cross-form rules that could not be auto-translated. Shown as an informational banner
   * on completion.
   */
  crossFormRuleCount: number
  /**
   * Count of rules being translated into DQ checks in the background.
   */
  translatingRuleCount: number
  /**
   * Human-readable parser name (e.g. "Medidata Rave ALS", "Veeva SDS").
   */
  parserLabel: string
  onClose: () => void
}

export default function CreationProgressStep({
  forms,
  selectedForms,
  progress,
  isComplete,
  crossFormRuleCount,
  translatingRuleCount,
  parserLabel,
  onClose,
}: CreationProgressStepProps) {
  const entries = forms.filter((f) => selectedForms.has(f.name))
  const createdCount = Array.from(progress.values()).filter((p) => p.status === "created").length
  const failedCount = Array.from(progress.values()).filter((p) => p.status === "failed").length

  return (
    <SpaceBetween size="l">
      {isComplete && (
        <Box variant="p">
          {createdCount} DTA{createdCount !== 1 ? "s" : ""} created
          {failedCount > 0 && `, ${failedCount} failed`}
        </Box>
      )}

      {isComplete && translatingRuleCount > 0 && (
        <Alert type="info">
          {translatingRuleCount} {parserLabel} validation rule
          {translatingRuleCount !== 1 ? "s" : ""} are being translated into data quality checks.
          Review them in the DQ Rules tab.
        </Alert>
      )}

      {isComplete && crossFormRuleCount > 0 && (
        <Alert type="info">
          {crossFormRuleCount} cross-form rule{crossFormRuleCount !== 1 ? "s" : ""} could not be
          translated (cross-form rule translation is not yet supported).
        </Alert>
      )}

      <Table<ParsedFormResult>
        variant="embedded"
        items={entries}
        trackBy="name"
        columnDefinitions={[
          {
            id: "form",
            header: "Form",
            cell: (form) => `${form.name} - ${form.label}`,
          },
          {
            id: "status",
            header: "Status",
            cell: (form) => {
              const p = progress.get(form.name)
              return (
                <StatusIndicator type={statusIndicatorType(p?.status)}>
                  {statusLabel(p?.status)}
                </StatusIndicator>
              )
            },
          },
          {
            id: "link",
            header: "Link",
            cell: (form) => {
              const p = progress.get(form.name)
              if (p?.status === "created" && p.dta_id) {
                return <Link href={`/dtas/${p.dta_id}`}>View</Link>
              }
              if (p?.status === "failed") {
                return (
                  <Box color="text-status-error" variant="span">
                    {p.error}
                  </Box>
                )
              }
              return "-"
            },
          },
        ]}
      />

      {isComplete && (
        <Box float="right">
          <Button variant="primary" onClick={onClose}>
            Done
          </Button>
        </Box>
      )}
    </SpaceBetween>
  )
}

function statusLabel(status?: CreationStatus): string {
  switch (status) {
    case "creating":
      return "Creating..."
    case "created":
      return "Created"
    case "failed":
      return "Failed"
    default:
      return "Pending"
  }
}

function statusIndicatorType(status?: CreationStatus): StatusIndicatorProps.Type {
  switch (status) {
    case "creating":
      return "in-progress"
    case "created":
      return "success"
    case "failed":
      return "error"
    default:
      return "pending"
  }
}
